import { platform } from '@/platform';
import type { DeviceHealth, ExtendedDeviceHealth } from '@/types';

/**
 * Collect real device health metrics via the platform layer.
 *
 * On Tizen this returns true disk usage, RAM RSS, CPU load, firmware
 * version, and uptime. On web (dev) the underlying APIs give
 * approximations or -1 where unavailable; we convert sentinels to 0
 * for fields that show up directly in the dashboard.
 *
 * Async because Tizen systeminfo is callback-based — platform/tizen.ts
 * wraps it in promises.
 */
export async function collectHealth(): Promise<DeviceHealth> {
  const info = await platform.systemInfo.collect();

  return {
    // Percent 0-100. -1 → 0 for clean dashboard rendering.
    diskUsage: info.diskUsedPercent >= 0 ? info.diskUsedPercent : 0,
    // RAM usage as a percent of a notional 256 MB process budget. The
    // dashboard renders this as % so we can't ship raw bytes here
    // without changing the schema; the byte total is also in the
    // extended report below.
    ramUsage:
      info.ramUsedBytes >= 0
        ? Math.min(
            100,
            Math.round((info.ramUsedBytes / (256 * 1024 * 1024)) * 100),
          )
        : 0,
    uptime: Math.round(info.appUptimeHours * 100) / 100,
    softwareVersion: '0.1.0',
  };
}

/**
 * Extended health snapshot for the logger / dashboard once we ship the
 * full metrics endpoint. Carries everything `collectHealth` returns
 * PLUS the raw bytes, CPU load, firmware version, and resolution.
 */
export async function collectExtendedHealth(): Promise<ExtendedDeviceHealth> {
  const info = await platform.systemInfo.collect();
  const base = await collectHealth();
  return {
    ...base,
    diskFreeBytes: info.diskFreeBytes,
    ramUsedBytes: info.ramUsedBytes,
    cpuLoad: info.cpuLoad,
    firmwareVersion: info.firmwareVersion,
    deviceModel: info.deviceModel,
    displayResolution: info.displayResolution,
    platformKind: platform.kind,
  };
}
